import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FiAlertCircle,
    FiCheckCircle,
    FiDownload,
    FiFilm,
    FiRefreshCw,
} from "react-icons/fi";
import ActionButton from "./ActionButton";
import { FormCard } from "./FormCard";
import StageStepsBar from "./StageStepsBar";
import StageStepHeader from "./StageStepHeader";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
    clearVideoError,
    generateVideo,
    markVideoSaved,
    setVideoPlaybackUrl,
} from "../../../store/smileDesign/videoGenerationSlice";
import { setRemainingCredits } from "../../../store/smileDesign/smileGenerationSlice";
import { getBoxFile } from "../../../utils/smileThumbnail";
import type { HistoryItem } from "../../../types/smileDesign/historyItem";
import { syncSmileImages } from "../../../store/smileDesign/smileCasesSlice";
import { fetchBoxAccessToken } from "../../../store/common/authSlice";
import { uploadSmileVideoToCase } from "./saveSmileVideo";

export function UpcomingVideoStage() {
    return (
        <div className="rounded-2xl sm:rounded-3xl border border-white/[0.07] bg-[#212226] px-6 py-16 sm:py-20 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-400/20 bg-amber-400/10">
                <FiFilm className="h-6 w-6 text-amber-300" />
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-amber-300">
                Upcoming feature
            </p>
            <h2 className="mt-2 text-xl sm:text-2xl font-semibold text-[#F1F0EA]">
                Smile video presentations
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#8b8f8d]">
                8-second head-turn smile videos are coming soon. You will be
                able to animate your after and extended designs from here.
            </p>
        </div>
    );
}

interface Props {
    beforeImage: string | null;
    afterImage: string | null;
    afterImageName?: string;
    smileType?: string;
    patientName: string;
    smileId?: string;
    caseBoxId?: number;
    history?: HistoryItem[];
    selectedImage?: HistoryItem | null;
    onSelectAfter?: (item: HistoryItem) => void;
    onComplete: () => void;
}

type VideoSourceKind = "after" | "extended";

function SourceTile({
    image,
    title,
    badge,
    selected,
    disabled,
    onClick,
}: {
    image: string;
    title: string;
    badge: string;
    selected: boolean;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`text-left rounded-xl overflow-hidden border transition-all ${
                selected
                    ? "border-[#2fa9bf] ring-1 ring-[#2fa9bf]/40"
                    : "border-white/10 hover:border-white/25"
            } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
        >
            <div className="relative aspect-[4/3] bg-[#181818]">
                <img
                    src={image}
                    alt={`${title} ${badge}`}
                    className="w-full h-full object-cover"
                />
                <span
                    className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                        badge === "Extended"
                            ? "bg-violet-500/90 text-white"
                            : "bg-[#2fa9bf] text-[#08151A]"
                    }`}
                >
                    {badge}
                </span>
            </div>
            <p className="px-2.5 py-2 text-xs font-semibold text-[#F1F0EA] truncate">
                {title}
            </p>
        </button>
    );
}
async function urlToImageFile(url: string, filename: string): Promise<File> {
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error("Failed to read the selected smile image");
    }
    const blob = await res.blob();
    if (!blob.size) {
        throw new Error("Selected smile image is empty");
    }
    const type = blob.type?.startsWith("image/") ? blob.type : "image/png";
    const name = /\.(jpe?g|png|webp)$/i.test(filename)
        ? filename
        : `${filename}.png`;
    return new File([blob], name, { type });
}

export default function VideoGenerationStage({
    beforeImage,
    afterImage,
    afterImageName,
    smileType,
    patientName,
    smileId,
    caseBoxId,
    history = [],
    selectedImage = null,
    onSelectAfter,
    onComplete,
}: Props) {
    const dispatch = useAppDispatch();
    const { user, boxAccessToken } = useAppSelector((state) => state.auth);
    const { loading: generating, videoUrl, boxFileId, saved: videoSaved, error } = useAppSelector(
        (state) => state.videoGeneration
    );

    const [loopPlayback, setLoopPlayback] = useState(true);
    const [localError, setLocalError] = useState<string | null>(null);
    const [savingVideo, setSavingVideo] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [sourceKind, setSourceKind] = useState<VideoSourceKind>("extended");
    const videoRef = useRef<HTMLVideoElement>(null);
    const playbackObjectUrlRef = useRef<string | null>(null);

    const afterChoices = useMemo(
        () => history.filter((item) => item.image),
        [history]
    );

    const sourceOptions = useMemo(() => {
        const options: {
            id: string;
            item: HistoryItem;
            kind: VideoSourceKind;
            image: string;
            title: string;
            badge: "After" | "Extended";
        }[] = [];

        for (const item of afterChoices) {
            options.push({
                id: `${item.imageName}::after`,
                item,
                kind: "after",
                image: item.image,
                title: item.smileType,
                badge: "After",
            });
            if (item.restored?.image) {
                options.push({
                    id: `${item.imageName}::extended`,
                    item,
                    kind: "extended",
                    image: item.restored.image,
                    title: item.smileType,
                    badge: "Extended",
                });
            }
        }

        return options;
    }, [afterChoices]);

    const activeAfter = useMemo(() => {
        const match = afterChoices.find(
            (item) => item.image === selectedImage?.image
        );
        return match ?? afterChoices[0] ?? null;
    }, [selectedImage, afterChoices]);

    const usingRestored =
        sourceKind === "extended" && !!activeAfter?.restored?.image;
    const sourceImage = usingRestored
        ? activeAfter?.restored?.image ?? null
        : activeAfter?.image ?? afterImage;
    const sourceLabel = usingRestored ? "Extended restore" : "After";
    const canGenerate = !!sourceImage;
    const generatingBusy = generating || preparing;
    const videoReady = !!videoUrl || !!boxFileId;
    const canSaveToCase = videoReady && !!smileId && !!caseBoxId;
    const displayError = localError || error;

    useEffect(() => {
        if (selectedImage || !afterChoices[0] || !onSelectAfter) return;
        onSelectAfter(afterChoices[0]);
    }, [selectedImage, afterChoices, onSelectAfter]);

    useEffect(() => {
        if (!activeAfter) return;
        if (sourceKind === "extended" && !activeAfter.restored?.image) {
            setSourceKind("after");
        }
    }, [activeAfter, sourceKind]);

    const selectSource = useCallback(
        (item: HistoryItem, kind: VideoSourceKind) => {
            onSelectAfter?.(item);
            setSourceKind(kind);
        },
        [onSelectAfter]
    );

    useEffect(() => {
        if (!boxFileId || !boxAccessToken) return;

        let cancelled = false;
        setPreviewLoading(true);
        setLocalError(null);

        (async () => {
            try {
                const blob = await getBoxFile(boxFileId, boxAccessToken);
                if (cancelled) return;
                if (playbackObjectUrlRef.current) {
                    URL.revokeObjectURL(playbackObjectUrlRef.current);
                    playbackObjectUrlRef.current = null;
                }
                const objectUrl = URL.createObjectURL(blob);
                playbackObjectUrlRef.current = objectUrl;
                dispatch(setVideoPlaybackUrl(objectUrl));
            } catch (e) {
                console.error("Failed to load video from Box", e);
                if (!cancelled) {
                    setLocalError(
                        "Video is on the case, but the preview failed to load. Refresh the page to try again."
                    );
                }
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [boxFileId, boxAccessToken, dispatch]);

    useEffect(() => {
        return () => {
            if (playbackObjectUrlRef.current) {
                URL.revokeObjectURL(playbackObjectUrlRef.current);
                playbackObjectUrlRef.current = null;
            }
        };
    }, []);

    const handleGenerate = useCallback(async () => {
        if (!canGenerate || !sourceImage || !user?.user_id) return;

        setLocalError(null);
        setPreparing(true);

        try {
            const file = await urlToImageFile(
                sourceImage,
                `${patientName || "Patient"}-${smileType || "smile"}-${
                    usingRestored ? "extended" : "after"
                }.png`
            );

            const result = await dispatch(
                generateVideo({
                    user_id: String(user.user_id),
                    file,
                    smile_id: smileId,
                    resolution: "720p",
                    durationSeconds: 8,
                    style: "smile-presentation",
                })
            );

            if (generateVideo.fulfilled.match(result)) {
                const credits = result.payload.data?.remainingCredits;
                if (credits !== undefined) {
                    dispatch(setRemainingCredits(credits));
                }
                onComplete();
            }
        } catch (err) {
            setLocalError(
                err instanceof Error
                    ? err.message
                    : "Failed to prepare the selected smile image"
            );
        } finally {
            setPreparing(false);
        }
    }, [
        canGenerate,
        sourceImage,
        user?.user_id,
        patientName,
        smileType,
        smileId,
        usingRestored,
        dispatch,
        onComplete,
    ]);

    const handleSaveToCase = useCallback(async () => {
        if (!canSaveToCase || !smileId || !caseBoxId) {
            setLocalError("Open a case before saving the video.");
            return;
        }
        if (videoSaved && boxFileId) return;
        if (!videoUrl) {
            setLocalError("Wait for the video preview to load, then save.");
            return;
        }

        setLocalError(null);
        setSavingVideo(true);
        try {
            const accessToken = await dispatch(
                fetchBoxAccessToken(true)
            ).unwrap();
            const uploaded = await uploadSmileVideoToCase({
                videoUrl,
                patientName,
                caseBoxId,
                accessToken,
            });
            await dispatch(
                syncSmileImages({
                    smileId,
                    imageData: [uploaded],
                })
            ).unwrap();
            dispatch(
                markVideoSaved({
                    boxFileId: Number(uploaded.box_id),
                    fileName: uploaded.name,
                })
            );
            dispatch(clearVideoError());
        } catch (err) {
            setLocalError(
                typeof err === "string"
                    ? err
                    : err instanceof Error
                      ? err.message
                      : "Failed to save video to this case"
            );
        } finally {
            setSavingVideo(false);
        }
    }, [
        canSaveToCase,
        smileId,
        caseBoxId,
        videoSaved,
        boxFileId,
        videoUrl,
        patientName,
        dispatch,
    ]);

    const handleDownload = useCallback(() => {
        if (!videoUrl) return;
        const link = document.createElement("a");
        link.href = videoUrl;
        link.download = `${patientName || "smile"}-new-smile-8s.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [videoUrl, patientName]);

    const videoActiveStep = !sourceImage
        ? 1
        : generatingBusy
          ? 3
          : videoReady
            ? 4
            : 2;

    const videoStepCopy: Record<number, { title: string; description: string }> = {
        1: {
            title: "Select image for video",
            description:
                "Choose the after smile (or extended restore) that will become the video.",
        },
        2: {
            title: "Review setup",
            description:
                "Confirm the before and after source, then start the 8-second smile video.",
        },
        3: {
            title: "Generate video",
            description:
                "Create an 8-second clip of the patient turning their head and smiling.",
        },
        4: {
            title: "Save video",
            description:
                "Save the finished smile video to this case.",
        },
    };

    return (
        <div className="space-y-4 sm:space-y-6">
            <StageStepsBar
                steps={[
                    { id: 1, label: "Select" },
                    { id: 2, label: "Setup" },
                    { id: 3, label: "Generate" },
                    { id: 4, label: "Save" },
                ]}
                activeStep={videoActiveStep}
                completedSteps={
                    new Set(
                        [
                            sourceImage ? 1 : null,
                            sourceImage ? 2 : null,
                            videoReady ? 3 : null,
                            videoSaved ? 4 : null,
                        ].filter((n): n is number => n !== null)
                    )
                }
            />

            <StageStepHeader
                title={videoStepCopy[videoActiveStep].title}
                description={videoStepCopy[videoActiveStep].description}
            />

            {!canGenerate && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
                    <FiFilm className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-300/90">
                        Select an after smile from Stage 1. That design is
                        animated into an 8-second clip of the patient turning
                        their head and smiling fully.
                    </p>
                </div>
            )}

            {displayError && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-500/5 border border-red-500/15">
                    <FiAlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300/90">{displayError}</p>
                </div>
            )}

            <FormCard
                title="Select image for video"
                hint={`${sourceOptions.length} image${sourceOptions.length === 1 ? "" : "s"} · 8s · 720p`}
            >
                {sourceOptions.length === 0 ? (
                    <p className="text-sm text-[#8b8f8d]">
                        Generate after smiles in Stage 1. Extended restores
                        from Stage 2 will appear beside those afters.
                    </p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {sourceOptions.map((option) => {
                            const selected =
                                activeAfter?.image === option.item.image &&
                                (option.kind === "extended"
                                    ? usingRestored
                                    : !usingRestored);
                            return (
                                <SourceTile
                                    key={option.id}
                                    image={option.image}
                                    title={option.title}
                                    badge={option.badge}
                                    selected={selected}
                                    disabled={generatingBusy}
                                    onClick={() =>
                                        selectSource(
                                            option.item,
                                            option.kind
                                        )
                                    }
                                />
                            );
                        })}
                    </div>
                )}
            </FormCard>

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 sm:gap-6">
                <div className="xl:col-span-2 space-y-4">
                    <FormCard title="Selected source">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <p className="text-xs font-medium text-[#8b8f8d] uppercase tracking-wide">
                                    Before
                                </p>
                                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-white/5 bg-[#181818]">
                                    {beforeImage ? (
                                        <img
                                            src={beforeImage}
                                            alt="Before"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
                                            No input photo
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <p className="text-xs font-medium text-[#8b8f8d] uppercase tracking-wide">
                                    Using · {sourceLabel}
                                </p>
                                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-[#2fa9bf]/20 bg-[#181818] ring-1 ring-[#2fa9bf]/10">
                                    {sourceImage ? (
                                        <img
                                            src={sourceImage}
                                            alt={sourceLabel}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
                                            Select an image above
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-[#2fa9bf] truncate">
                                    {activeAfter?.smileType ||
                                        smileType ||
                                        afterImageName}
                                </p>
                            </div>
                        </div>
                    </FormCard>

                    <FormCard title="Playback">
                        <label className="flex items-center gap-3 cursor-pointer group">
                            <div
                                className={`relative w-10 h-6 rounded-full transition-colors ${
                                    loopPlayback ? "bg-[#2fa9bf]" : "bg-[#303030]"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={loopPlayback}
                                    onChange={(e) =>
                                        setLoopPlayback(e.target.checked)
                                    }
                                    className="sr-only"
                                />
                                <span
                                    className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                        loopPlayback ? "left-5" : "left-1"
                                    }`}
                                />
                            </div>
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                                Loop video
                            </span>
                        </label>
                    </FormCard>

                    <div className="flex gap-2">
                        <ActionButton
                            onClick={() => void handleGenerate()}
                            loading={generatingBusy}
                            disabled={!canGenerate || generatingBusy || savingVideo}
                            text="Generate 8s smile video"
                            loadingText="Creating 8s video…"
                        />
                    </div>
                    <ActionButton
                        onClick={() => void handleSaveToCase()}
                        loading={savingVideo}
                        disabled={
                            !canSaveToCase ||
                            !videoUrl ||
                            videoSaved ||
                            generatingBusy ||
                            savingVideo
                        }
                        text={videoSaved ? "Saved with case" : "Save video to case"}
                        loadingText="Saving video…"
                        icon="save"
                        variant={videoSaved ? "ghost" : "primary"}
                    />
                    {videoSaved && (
                        <p className="text-xs text-emerald-400/90 text-center flex items-center justify-center gap-1.5">
                            <FiCheckCircle className="w-3 h-3" />
                            Video is stored with this case
                        </p>
                    )}
                </div>

                <div className="xl:col-span-3">
                    <div className="h-full rounded-2xl sm:rounded-3xl border border-[#2fa9bf]/20 bg-[#303030] p-3 sm:p-6 shadow-[0_0_30px_rgba(47,169,191,0.08)]">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                            <div>
                                <h3 className="text-lg sm:text-xl font-bold">
                                    Video Preview
                                </h3>
                                <p className="text-xs sm:text-sm text-[#8b8f8d] mt-0.5">
                                    {videoReady
                                        ? "Head turn · full smile · 8 seconds"
                                        : "Your 8s smile presentation will appear here"}
                                </p>
                            </div>
                            {videoUrl && !generatingBusy && (
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#101211] border border-white/10 text-sm font-medium hover:border-[#2fa9bf]/30 transition-colors"
                                >
                                    <FiDownload className="w-4 h-4" />
                                    Download MP4
                                </button>
                            )}
                        </div>

                        <div className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden border border-white/5 bg-[#080808]">
                            {videoUrl && !generatingBusy ? (
                                <div className="relative w-full h-full">
                                    <video
                                        ref={videoRef}
                                        src={videoUrl}
                                        controls
                                        loop={loopPlayback}
                                        autoPlay
                                        muted
                                        playsInline
                                        className="w-full h-full object-contain"
                                    />
                                    <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-sm border border-white/10 text-xs">
                                        <span className="text-[#2fa9bf] font-semibold">
                                            {patientName || "Patient"}
                                        </span>
                                        <span className="text-gray-400 mx-2">
                                            ·
                                        </span>
                                        <span className="text-gray-300">
                                            8s · 720p
                                        </span>
                                    </div>
                                </div>
                            ) : generatingBusy || previewLoading ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                                    <div className="relative w-16 h-16">
                                        <div className="absolute inset-0 rounded-full border-2 border-[#2fa9bf]/20" />
                                        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#2fa9bf] animate-spin" />
                                        <FiFilm className="absolute inset-0 m-auto w-6 h-6 text-[#2fa9bf]" />
                                    </div>
                                    <p className="text-sm font-medium text-white">
                                        {generatingBusy
                                            ? "Generating 8s smile video…"
                                            : "Loading video preview…"}
                                    </p>
                                    <p className="text-xs text-[#8b8f8d]">
                                        {generatingBusy
                                            ? "Head movement and full smile · may take several minutes"
                                            : "Fetching the saved MP4 from the case"}
                                    </p>
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                                    <FiFilm className="w-8 h-8 text-[#8b8f8d]" />
                                    <p className="text-sm text-[#8b8f8d] max-w-xs">
                                        Generate an 8-second 720p clip of the
                                        patient turning their head and smiling
                                        fully to show the new teeth.
                                    </p>
                                </div>
                            )}
                        </div>

                        {videoReady && !generatingBusy && (
                            <button
                                type="button"
                                onClick={() => void handleGenerate()}
                                disabled={savingVideo}
                                className="flex items-center justify-center gap-2 w-full mt-4 py-3 rounded-xl border border-white/10 bg-[#101211] text-sm font-medium hover:border-[#2fa9bf]/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <FiRefreshCw className="w-4 h-4" />
                                Regenerate
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
